import {
  BOM,
  COMMENT_MARKER,
  FIXED_COLUMN_COUNT,
  GROUPS,
  HEADER_FIXED,
  ROW_KINDS,
  TOTAL_COLUMN_COUNT,
} from "./constants";
import { splitCsvLine, splitLines } from "./csvLowLevel";
import { parseMonthHeader } from "./monthOrder";
import type {
  BudgetCsvOptions,
  CategoryGroup,
  MonthKey,
  ParseResult,
  ParsedRow,
  RowKind,
  ValidationIssue,
} from "./types";

const isCommentLine = (line: string): boolean =>
  line.trimStart().startsWith(COMMENT_MARKER);

const isBlankLine = (line: string): boolean => line.trim() === "";

interface HeaderInfo {
  monthOrder: MonthKey[];
  headerLineNumber: number;
}

function parseHeader(
  fields: string[],
  lineNumber: number,
  errors: ValidationIssue[],
): MonthKey[] | null {
  if (fields.length !== TOTAL_COLUMN_COUNT) {
    errors.push({
      severity: "error",
      lineNumber,
      message: `ヘッダー列数が不正です (期待 ${TOTAL_COLUMN_COUNT} 列、実際 ${fields.length} 列)`,
    });
    return null;
  }
  for (let i = 0; i < FIXED_COLUMN_COUNT; i++) {
    if (fields[i].trim() !== HEADER_FIXED[i]) {
      errors.push({
        severity: "error",
        lineNumber,
        message: `ヘッダー ${i + 1} 列目は "${HEADER_FIXED[i]}" 必須 (実際: "${fields[i]}")`,
      });
      return null;
    }
  }
  const monthOrder: MonthKey[] = [];
  const seen = new Set<MonthKey>();
  for (let i = FIXED_COLUMN_COUNT; i < TOTAL_COLUMN_COUNT; i++) {
    const key = parseMonthHeader(fields[i]);
    if (!key) {
      errors.push({
        severity: "error",
        lineNumber,
        message: `ヘッダー月列 "${fields[i]}" の形式が不正です ("4月" 形式必須)`,
      });
      return null;
    }
    if (seen.has(key)) {
      errors.push({
        severity: "error",
        lineNumber,
        message: `月ヘッダー "${fields[i]}" が重複しています`,
      });
      return null;
    }
    seen.add(key);
    monthOrder.push(key);
  }
  return monthOrder;
}

/**
 * 全角の数字・小数点・マイナスを半角へ正規化する。
 * 取り込み時に半角／全角の差異を吸収するため。
 * - 全角数字 ０-９ (U+FF10-FF19) → 0-9
 * - 全角ピリオド ． (U+FF0E) → .
 * - 全角マイナス － (U+FF0D) → -
 */
function normalizeNumericText(raw: string): string {
  return raw
    .replace(/[０-９]/g, (ch) =>
      String.fromCharCode(ch.charCodeAt(0) - 0xfee0),
    )
    .replace(/．/g, ".")
    .replace(/－/g, "-");
}

function parseMonthCell(
  raw: string,
  lineNumber: number,
  monthLabel: string,
  errors: ValidationIssue[],
): number | null {
  const trimmed = normalizeNumericText(raw.trim());
  if (trimmed === "") return null;
  // 小数も受理する。小数点以下は後段で切り捨てる。
  if (!/^-?\d+(\.\d+)?$/.test(trimmed)) {
    errors.push({
      severity: "error",
      lineNumber,
      field: monthLabel,
      message: `月セル "${monthLabel}" は数値のみ (実際: "${raw}")`,
    });
    return null;
  }
  // 小数点以下は切り捨て (0 方向への切り捨て)。
  const value = Math.trunc(Number(trimmed));
  if (value < 0) {
    errors.push({
      severity: "error",
      lineNumber,
      field: monthLabel,
      message: `月セル "${monthLabel}" は 0 以上の整数のみ (実際: ${value})`,
    });
    return null;
  }
  return value;
}

function parseDataRow(
  fields: string[],
  lineNumber: number,
  monthOrder: MonthKey[],
  errors: ValidationIssue[],
): ParsedRow | null {
  // 末尾の連続する空フィールドは Excel 編集等で混入しがちなので、TOTAL_COLUMN_COUNT まで切り詰めて許容する
  while (
    fields.length > TOTAL_COLUMN_COUNT &&
    fields[fields.length - 1].trim() === ""
  ) {
    fields.pop();
  }
  if (fields.length !== TOTAL_COLUMN_COUNT) {
    errors.push({
      severity: "error",
      lineNumber,
      message: `列数が不正です (期待 ${TOTAL_COLUMN_COUNT} 列、実際 ${fields.length} 列)`,
    });
    return null;
  }
  const fiscalYear = fields[0].trim();
  const groupRaw = fields[1].trim();
  const categoryGroup = fields[2].trim();
  const category = fields[3].trim();
  const rowKindRaw = fields[4].trim();

  if (!GROUPS.includes(groupRaw as CategoryGroup)) {
    errors.push({
      severity: "error",
      lineNumber,
      field: "収入/支出",
      message: `"収入/支出" は "収入" または "支出" のみ (実際: "${groupRaw}")`,
    });
    return null;
  }
  if (!ROW_KINDS.includes(rowKindRaw as RowKind)) {
    errors.push({
      severity: "error",
      lineNumber,
      field: "予算/実績",
      message: `"予算/実績" は "予算" または "実績" のみ (実際: "${rowKindRaw}")`,
    });
    return null;
  }

  const months: Record<MonthKey, number | null> = {};
  for (let i = 0; i < monthOrder.length; i++) {
    const raw = fields[FIXED_COLUMN_COUNT + i];
    const label = `${parseInt(monthOrder[i], 10)}月`;
    months[monthOrder[i]] = parseMonthCell(raw, lineNumber, label, errors);
  }

  return {
    lineNumber,
    fiscalYear,
    group: groupRaw as CategoryGroup,
    categoryGroup,
    category,
    rowKind: rowKindRaw as RowKind,
    months,
  };
}

/**
 * CSV テキストをパースして ParsedRow[] を返す。
 * 構造的に致命的な問題 (BOM/ヘッダー/列数) は ParseFailure として早期リターンし、
 * 個別行の値検査は validator.ts 側に委譲する。
 */
export function parseBudgetCsv(
  text: string,
  _options: BudgetCsvOptions,
): ParseResult {
  const errors: ValidationIssue[] = [];

  if (text.length === 0) {
    return {
      ok: false,
      errors: [{ severity: "error", lineNumber: 0, message: "ファイルが空です" }],
    };
  }
  // BOM はあれば剥がす。Excel/メモ帳由来でも、それ以外でも受け入れる。
  const stripped = text.startsWith(BOM) ? text.slice(BOM.length) : text;
  const lines = splitLines(stripped);

  let header: HeaderInfo | null = null;
  const rows: ParsedRow[] = [];

  for (let i = 0; i < lines.length; i++) {
    const line = lines[i];
    const lineNumber = i + 1;
    if (isBlankLine(line)) continue;
    if (isCommentLine(line)) continue;
    const fields = splitCsvLine(line);
    if (!header) {
      // ヘッダー (1列目 = "年度") より前の説明行 (■/・/【】 等) はスキップする。
      if ((fields[0] ?? "").trim() !== HEADER_FIXED[0]) continue;
      const monthOrder = parseHeader(fields, lineNumber, errors);
      if (!monthOrder) {
        return { ok: false, errors };
      }
      header = { monthOrder, headerLineNumber: lineNumber };
      continue;
    }
    const row = parseDataRow(fields, lineNumber, header.monthOrder, errors);
    if (row) rows.push(row);
  }

  if (!header) {
    return {
      ok: false,
      errors: [
        ...errors,
        {
          severity: "error",
          lineNumber: 0,
          message: "ヘッダー行が見つかりません",
        },
      ],
    };
  }
  if (errors.length > 0) {
    return { ok: false, errors };
  }
  return { ok: true, rows, monthOrder: header.monthOrder };
}
